import * as z from 'zod';
import { FIRST_PAGE } from '@noor/config';

/**
 * Search-service settings are *versioned*: every save creates a new version and
 * closes the previous one (`end_at`). Three resources share that lifecycle —
 * delivery settings, ETA settings and multiorder parameters.
 *
 * int64 ids travel as strings, but a gateway may hand back numbers; both are
 * normalised to strings at the boundary.
 */
const idLike = z.union([z.string(), z.number()]).transform(String);

const versionMeta = {
  id: idLike,
  start_at: z.string().nullish(),
  end_at: z.string().nullish(),
  created_by: idLike.nullish(),
  created_at: z.string().nullish(),
};

/* ----------------------------- Delivery settings ---------------------------- */

export const deliverySettingsVersionSchema = z.object({
  ...versionMeta,
  average_minutes_per_km: z.number(),
  average_minutes_per_km_for_car: z.number(),
  possible_margin_of_error_pct: z.number(),
  car_equipment_id: idLike.nullish(),
  default_min_couriers_found: z.number(),
  min_courier_for_eval: z.number(),
  minute_per_origin: z.number(),
  minute_per_destination: z.number(),
  return_origin_minute: z.number(),
  use_map: z.string().nullish(),
  use_map_minutes: z.boolean().nullish(),
});
export type DeliverySettingsVersion = z.infer<typeof deliverySettingsVersionSchema>;

const required = { error: 'settings:errors.required' } as const;
const integer = { error: 'settings:errors.integer' } as const;

/** Whole number ≥ `min` — the RHF number control stores `null` when emptied. */
const wholeNumber = (min: number) =>
  z
    .number(required)
    .int(integer)
    .min(min, { error: `settings:errors.${min > 0 ? 'positive' : 'negative'}` });

const percent = z
  .number(required)
  .min(0, { error: 'settings:errors.percent' })
  .max(100, { error: 'settings:errors.percent' });

/** Whole-number percent (the API stores it as int32). */
const wholePercent = wholeNumber(0).max(100, { error: 'settings:errors.percent' });

const decimalPace = z.number(required).positive({ error: 'settings:errors.positive' });

export const deliverySettingsFormSchema = z.object({
  average_minutes_per_km: wholeNumber(1),
  average_minutes_per_km_for_car: wholeNumber(1),
  car_equipment_id: z.string(required).min(1, required),
  minute_per_origin: wholeNumber(0),
  minute_per_destination: wholeNumber(0),
  return_origin_minute: wholeNumber(0),
  default_min_couriers_found: wholeNumber(1),
  min_courier_for_eval: wholeNumber(0),
  possible_margin_of_error_pct: wholePercent,
  use_map: z.string().trim(),
  use_map_minutes: z.boolean(),
});
export type DeliverySettingsFormValues = z.infer<typeof deliverySettingsFormSchema>;

/* -------------------------------- ETA settings ------------------------------- */

export const etaSettingsVersionSchema = z.object({
  ...versionMeta,
  minute_per_km_for_scooter: z.number(),
  minute_per_km_for_car: z.number(),
  origin_minute: z.number(),
  destination_minute: z.number(),
  is_client: z.boolean().nullish(),
  search_buffer: z.number(),
  deleted_by: idLike.nullish(),
});
export type EtaSettingsVersion = z.infer<typeof etaSettingsVersionSchema>;

export const etaSettingsFormSchema = z.object({
  minute_per_km_for_scooter: decimalPace,
  minute_per_km_for_car: decimalPace,
  origin_minute: wholeNumber(0),
  destination_minute: wholeNumber(0),
  search_buffer: wholeNumber(0),
  is_client: z.boolean(),
});
export type EtaSettingsFormValues = z.infer<typeof etaSettingsFormSchema>;

/* ---------------------------- Multiorder parameters --------------------------- */

export const multiorderParamsVersionSchema = z.object({
  ...versionMeta,
  max_distance_between_dest_and_courier_m: z.number(),
  courier_car_radius: z.number(),
  courier_scooter_radius: z.number(),
  point_differ_percent: z.number(),
  point_radius_m: z.number(),
});
export type MultiorderParamsVersion = z.infer<typeof multiorderParamsVersionSchema>;

export const multiorderParamsFormSchema = z.object({
  courier_scooter_radius: wholeNumber(1),
  courier_car_radius: wholeNumber(1),
  max_distance_between_dest_and_courier_m: wholeNumber(1),
  point_radius_m: wholeNumber(0),
  point_differ_percent: percent,
});
export type MultiorderParamsFormValues = z.infer<typeof multiorderParamsFormSchema>;

/* ----------------------------------- History -------------------------------- */

export const HISTORY_PAGE_SIZE = 10;

export const SEARCH_SETTINGS_TABS = ['active', 'history'] as const;
export type SearchSettingsTab = (typeof SEARCH_SETTINGS_TABS)[number];

/**
 * URL search params of a settings page — the URL is the source of truth for the
 * tab and the history paging. An unknown `tab` falls back to the form.
 */
export const searchSettingsSearchSchema = z.object({
  tab: z.enum(SEARCH_SETTINGS_TABS).catch('active'),
  page: z.coerce.number().int().positive().default(FIRST_PAGE),
  pageSize: z.coerce.number().int().positive().default(HISTORY_PAGE_SIZE),
});
export type SearchSettingsSearch = z.infer<typeof searchSettingsSearchSchema>;

export interface VersionHistoryPage<TVersion> {
  list: TVersion[];
  total: number;
  pageCount: number;
}
